import React from 'react';

export interface MentionEntity {
  type: string;
  id: string;
  displayName: string;
}
export type MentionParserFunction = (entity: MentionEntity) => string;

export const MENTION_REGEX = /@\{([^}]+)\}\{([^}]+)\}\{([^}]+)\}/g;

export const parseMentionString = (text: string, onParseMention?: (entity: MentionEntity) => string): string => {
  if (!text) return '';

  return text.replace(MENTION_REGEX, (match, entityType, entityId, displayName) => {
    const entity: MentionEntity = {
      type: entityType,
      id: entityId,
      displayName,
    };

    if (onParseMention) {
      return onParseMention(entity);
    }

    // Default parsing behavior
    return `@${displayName}`;
  });
};

export const createMentionString = (entityType: string, entityId: string, displayName: string): string => {
  return `@{${entityType}}{${entityId}}{${displayName}}`;
};

export const renderMessageWithMentions = (
  text: string | undefined,
  onParseMention?: MentionParserFunction,
): JSX.Element[] => {
  if (!text) return [];

  const parts: JSX.Element[] = [];
  let lastIndex = 0;

  // Reset regex for each execution
  const regex = new RegExp(MENTION_REGEX);
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const [fullMatch, entityType, entityId, displayName] = match;
    const matchIndex = match.index;

    // Add any text that comes before this mention
    if (matchIndex > lastIndex) {
      parts.push(
        React.createElement('span', {
          key: `text-${lastIndex}-${matchIndex}`,
          children: text.slice(lastIndex, matchIndex),
        }),
      );
    }

    // Process the mention
    const entity: MentionEntity = {
      type: entityType,
      id: entityId,
      displayName,
    };

    const mentionText = onParseMention ? onParseMention(entity) : `@${displayName}`;

    parts.push(
      React.createElement('span', {
        key: `mention-${matchIndex}-${entityId}`,
        className: 'mention',
        children: mentionText,
      }),
    );

    lastIndex = matchIndex + fullMatch.length;
  }

  // Add any remaining text after the last mention
  if (lastIndex < text.length) {
    parts.push(
      React.createElement('span', {
        key: `text-${lastIndex}-end`,
        children: text.slice(lastIndex),
      }),
    );
  }

  return parts;
};
